<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { POWERED_BY_REL, poweredByPageHref, type PoweredBySurface } from '~/utils/booking/metadata'

const props = withDefaults(defineProps<{
  surface: PoweredBySurface
  token?: string
  workspaceId?: string
  placement?: 'page' | 'embed'
}>(), { placement: 'page' })
const via = ref<string>()
const href = computed(() => poweredByPageHref(props.placement, props.surface, via.value))
// Non-blocking enrichment; the ordinary UTM link works even without JavaScript or when the lookup fails.
if (import.meta.client) watch(() => [props.surface, props.token, props.workspaceId], async (_, __, cleanup) => {
  via.value = undefined
  if (!props.token && !props.workspaceId) return
  const abort = new AbortController()
  cleanup(() => abort.abort())
  const timeout = setTimeout(() => abort.abort(), 3000)
  try {
    const query = new URLSearchParams({ surface: props.surface })
    if (props.token) query.set('token', props.token)
    if (props.workspaceId) query.set('workspaceId', props.workspaceId)
    const response = await fetch(`/api/powered-by?${query}`, { signal: abort.signal })
    const data = await response.json()
    if (!abort.signal.aborted && typeof data.via === 'string') via.value = data.via
  }
  catch { /* Keep the usable UTM link. */ }
  finally { clearTimeout(timeout) }
}, { immediate: true })
</script>

<template>
  <a :href="href" target="_blank" :rel="POWERED_BY_REL" class="workspace-credit" :data-powered-by="surface">
    <slot>Powered by <strong>Workspace369</strong></slot>
  </a>
</template>

<style scoped>
.workspace-credit { display: inline-flex; align-items: center; justify-content: center; gap: .3em; padding: 8px 4px; color: inherit; font: 400 12px/1.5 system-ui, sans-serif; text-decoration: none; }
.workspace-credit strong { font-weight: 600; }
.workspace-credit:hover { text-decoration: underline; }
.workspace-credit:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; border-radius: 4px; }
@media print { .workspace-credit { display: none; } }
</style>
